<template>
  <div id="product-detail__offer-list">
    <div v-if="isProductDetailVisible" class="variant-block" :class="{ 'header-visible': headerShow }">
      <section v-if="headerShow" class="variant-block-header" :class="{ stuck: headerSticky }" data-test="header">
        <slot name="header"></slot>
      </section>
      <p-variant-list
        :variant-list="variantListWithOffer"
        class="variants-with-offers"
        data-test="variant-with-offers"
      ></p-variant-list>
      <p-discount-info :product-id="productId" />
    </div>
    <p-variant-without-offer
      v-if="variantListWithoutOffer && variantListWithoutOffer.length"
      class="variant-without-offer"
      :variant-list="variantListWithoutOffer"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { hasOffers } from '../../../services/product.business'
import { useShop } from '@b2ag/catalog/src/useShop'
import { useProduct } from '@b2ag/catalog/src/useProduct'
import { useVariants } from '../../../composables/useVariants'
import PVariantList from '../PVariantList/PVariantList.vue'
import PVariantWithoutOffer from '../PVariantWithoutOffer/PVariantWithoutOffer.vue'
import PDiscountInfo from '../PDiscountInfo/PDiscountInfo.vue'
import { filterVariantsFromProfile } from '.'

export default defineComponent({
  name: 'PVariantBlock',
  components: { PVariantList, PVariantWithoutOffer, PDiscountInfo },
  props: {
    productId: { type: String, required: true },
    headerShow: { type: Boolean, default: false },
    headerSticky: { type: Boolean, default: false },
  },
  setup(props) {
    const { variantListWithOffer, variantListWithoutOffer } = useShop(props.productId)
    const { product } = useProduct(props.productId)

    const { variantsToDisplay } = useVariants(product.value?.variants)
    const isOffersBlockVisible = computed(() => hasOffers(product.value))
    const isProductDetailVisible = computed(() => variantsToDisplay.length > 0 && isOffersBlockVisible.value)

    return {
      isProductDetailVisible,
      variantListWithOffer: filterVariantsFromProfile(variantListWithOffer),
      variantListWithoutOffer: filterVariantsFromProfile(variantListWithoutOffer),
      product,
    }
  },
})
</script>

<style lang="scss" scoped>
/* Safari doesn't support @counter-style but supports the nonstandard "asterisks" counter-style by default,
 * doing exactly what we want ; this will define asterisks for all the other browsers. */
@counter-style asterisks {
  system: symbolic;
  symbols: '*';
  suffix: ' ';
}

.variant-block {
  background: linear-gradient(90deg, var(--gradient-varient-offer-start), var(--gradient-varient-offer-end));
  color: var(--color-white);
  padding: var(--spacing-3);

  &.header-visible {
    padding-top: 0;
  }

  @media (min-width: $breakpoint-md) {
    margin: 0 calc((100vw - 100%) / -6);
    padding: 30px calc((100vw - 100%) / 6);
    border-radius: 10px;
  }

  @media (min-width: $breakpoint-xl) {
    border-radius: 20px;
  }

  @media (max-width: $breakpoint-sm) {
    padding: 30px 10px;
  }
}

.variant-block > h2 {
  text-align: center;
  font-size: 1em;
  text-transform: uppercase;
}

.variant-without-offer {
  margin-top: 3rem;
}

.variants-with-offers {
  counter-reset: discount-refs 0;
}

.variant-block > .variant-block-header {
  top: 10rem;
  margin: 0 -1rem 1rem;
  z-index: 98;
  box-shadow: -2px 2px 8px rgb(0 0 0 / 20%);

  &.stuck {
    position: sticky;
  }

  @media (min-width: $breakpoint-md) {
    top: 10.9rem;
    border-top-left-radius: 2rem;
    border-top-right-radius: 2rem;
    margin: 0 calc(-1 * (100vw - 100%) / 6) 1rem;
    padding: 2rem calc((100vw - 100%) / 6);
  }

  @media (min-width: $breakpoint-lg) {
    top: 12.4rem;
  }
}
</style>
